import React, { useState, useMemo } from 'react';
import { Pencil, X, Save } from 'lucide-react';
import { ventesAPI } from '../../services/api';
import { TYPE_CAISSE_OPTIONS, canSeeTypeCaisse } from '../../utils/typeCaisseLabels';

// §201 — Modal d'edition d'une vente (centrale / admin uniquement).
// Le bouton qui l'ouvre n'est rendu que pour les admins (canEditVente),
// mais on re-garde ici par defense en profondeur pour le selecteur type_caisse.
// Champs editables : date_vente, mode_paiement, type_caisse (admin),
// prix_vente, remise_montant, remise_pourcentage, montant_final (surchargeable),
// commentaire. Contrat backend : PUT /api/ventes/:id.

// Normalise une date (potentiellement avec heure) en YYYY-MM-DD pour <input type=date>
const toDateStr = (d) => {
  if (!d) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
  const dt = new Date(d);
  if (isNaN(dt)) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const toNum = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

function EditVenteModal({ vente, onClose, onSaved }) {
  const role = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.role || ''; }
    catch { return ''; }
  }, []);
  const showTypeCaisse = canSeeTypeCaisse(role);

  const [form, setForm] = useState({
    date_vente: toDateStr(vente.date_vente),
    mode_paiement: vente.mode_paiement || 'especes',
    type_caisse: vente.type_caisse || 'officielle',
    prix_vente: vente.prix_vente ?? '',
    remise_montant: vente.remise_montant ?? '',
    remise_pourcentage: vente.remise_pourcentage ?? '',
    commentaire: vente.commentaire ?? '',
  });

  // montant_final : calcule live, mais surchargeable manuellement par l'admin.
  // Tant que l'admin n'a pas touche le champ, il suit le calcul automatique.
  const computedMontantFinal = useMemo(() => {
    const px = toNum(form.prix_vente);
    const rm = toNum(form.remise_montant);
    const rp = toNum(form.remise_pourcentage);
    const m = px - rm - (px * rp / 100);
    return Math.round((m < 0 ? 0 : m) * 100) / 100;
  }, [form.prix_vente, form.remise_montant, form.remise_pourcentage]);

  const [montantTouched, setMontantTouched] = useState(false);
  const [montantOverride, setMontantOverride] = useState(
    vente.montant_final != null ? String(vente.montant_final) : ''
  );
  const montantFinal = montantTouched ? montantOverride : computedMontantFinal;

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        date_vente: form.date_vente || null,
        mode_paiement: form.mode_paiement,
        prix_vente: toNum(form.prix_vente),
        remise_montant: toNum(form.remise_montant),
        remise_pourcentage: toNum(form.remise_pourcentage),
        montant_final: toNum(montantFinal),
        commentaire: form.commentaire || null,
      };
      // type_caisse uniquement si admin (le backend rejette 403 sinon)
      if (showTypeCaisse) payload.type_caisse = form.type_caisse;

      const res = await ventesAPI.update(vente.id, payload);
      const updated = res.data?.vente || { ...vente, ...payload };
      onSaved(updated);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la modification de la vente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex justify-between items-center rounded-t-lg">
          <div className="flex items-center space-x-3">
            <Pencil className="h-6 w-6" />
            <h3 className="text-xl font-bold">Modifier la vente</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          {/* Rappel contexte vente */}
          <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-600">
            <span className="font-medium text-gray-900">{vente.bijou_nom}</span>
            {vente.client_nom && <> — {vente.client_nom}</>}
            {vente.boutique_nom && <> • {vente.boutique_nom}</>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de vente</label>
              <input
                type="date"
                value={form.date_vente}
                onChange={(e) => setField('date_vente', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
              <select
                value={form.mode_paiement}
                onChange={(e) => setField('mode_paiement', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {/* P6 caisse 11/06/2026 : mode = MOYEN de paiement ; la caisse
                    fiscale = champ type_caisse séparé (fin des especes_caisse1/2) */}
                <option value="especes">Espèces</option>
                <option value="carte">Carte</option>
                <option value="cheque">Chèque</option>
                <option value="virement">Virement bancaire</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>

          {showTypeCaisse && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de caisse</label>
              <select
                value={form.type_caisse}
                onChange={(e) => setField('type_caisse', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {TYPE_CAISSE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix de vente (DH)</label>
              <input
                type="text" inputMode="decimal" onFocus={(e) => e.target.select()}
                value={form.prix_vente}
                onChange={(e) => setField('prix_vente', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remise (DH)</label>
              <input
                type="text" inputMode="decimal" onFocus={(e) => e.target.select()}
                value={form.remise_montant}
                onChange={(e) => setField('remise_montant', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remise (%)</label>
              <input
                type="text" inputMode="decimal" onFocus={(e) => e.target.select()}
                value={form.remise_pourcentage}
                onChange={(e) => setField('remise_pourcentage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Montant final (DH)</label>
              {montantTouched && (
                <button
                  type="button"
                  onClick={() => { setMontantTouched(false); setMontantOverride(''); }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  ↺ Recalculer automatiquement
                </button>
              )}
            </div>
            <input
              type="text" inputMode="decimal" onFocus={(e) => e.target.select()}
              value={montantFinal}
              onChange={(e) => { setMontantTouched(true); setMontantOverride(e.target.value); }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-bold ${
                montantTouched ? 'border-amber-400 bg-amber-50' : 'border-gray-300 bg-gray-50'
              }`}
            />
            <p className="text-xs text-gray-400 mt-1">
              {montantTouched
                ? 'Valeur saisie manuellement (surcharge le calcul automatique).'
                : 'Calculé : prix − remise DH − (prix × remise %).'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire</label>
            <textarea
              value={form.commentaire}
              onChange={(e) => setField('commentaire', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Commentaire interne..."
            />
          </div>
        </div>

        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Enregistrement...</span>
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                <span>Enregistrer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditVenteModal;
